// src/App.js

import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuthContext } from "./AuthContext";
import { SeasonProvider } from "./contexts/SeasonContext";
import { logAccess } from "./api"; // Import logAccess
import "./styles/global.css";
import ReactGA from "react-ga4";
import AnalyticsTracker from "./components/AnalyticsTracker"; // New component

import "bootstrap/dist/css/bootstrap.min.css";

// Eager load only critical components
import Login from "./components/Login";
import Header from "./components/Header";
import Home from "./components/Home";
import Footer from "./components/Footer";

// Initialize Google Analytics
ReactGA.initialize("G-QY0HDWEQ1J");
if (process.env.NODE_ENV === "production" || process.env.REACT_APP_IS_LOCAL === "true") {
  console.log("GA initialized: G-QY0HDWEQ1J");
}

// Lazy load all pages - they're only loaded when user navigates to them
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const Fixtures = lazy(() => import("./pages/Fixtures"));
const Results = lazy(() => import("./pages/Results"));
const AllMatches = lazy(() => import("./pages/AllMatches"));
const Tables = lazy(() => import("./pages/Tables"));
const PR = lazy(() => import("./pages/PR"));
const ResourceAdmin = lazy(() => import("./pages/ResourceAdmin"));
const ResourceBookings = lazy(() => import("./pages/ResourceBookings"));
const VolunteerRoleAdmin = lazy(() => import("./pages/VolunteerRoleAdmin"));
const TeamAdmin = lazy(() => import("./pages/TeamAdmin"));
const MinisFixtures = lazy(() => import("./pages/MinisFixtures"));
const MinisAdmin = lazy(() => import("./pages/MinisAdmin"));
const Insights = lazy(() => import("./components/Insights"));
const WebsiteEmbedAdminPage = lazy(() => import("./pages/WebsiteEmbedAdminPage"));

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh'
  }}>
    <div className="spinner-border" role="status" style={{ color: '#d4af37' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function AppContent() {
  const { user, loading, roles, clubName, theme } = useAuthContext();

  useEffect(() => {
    if (user && !loading) {
      // Set User ID in Google Analytics (Safe: doesn't send email/name)
      ReactGA.set({ userId: user.uid });

      logAccess({
        club: clubName,
        email: user.email,
        role: roles && roles.length > 0 ? roles[0] : "user"
      }).catch(e => console.error("Failed to log access", e));
    }
  }, [user, loading, clubName, roles]);

  if (loading) {
    // Rugby/runner themed loading options
    const loadingOptions = [
      {
        emoji: '🏉',
        message: "Warming up the scrum... data is loading!"
      },
      {
        emoji: '👟',
        message: "Lacing up the boots... Getting ready for kickoff!"
      },
      {
        emoji: '⏳',
        message: "Waiting for the ref... The match will start soon!"
      },
      {
        emoji: '🚩',
        message: "Setting up the try line... Loading your club data!"
      },
      {
        emoji: '🏃‍♂️',
        message: "Running down the wing... Loading club data!"
      }
    ];
    // Pick a random option each time
    const randomIdx = Math.floor(Math.random() * loadingOptions.length);
    const { emoji, message } = loadingOptions[randomIdx];

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          borderRadius: '12px',
          backgroundColor: 'white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px',
          width: '90%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1.2rem' }}>{emoji}</div>
          <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: '#d4af37', marginBottom: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div style={{ fontSize: '1.1rem', color: '#555', marginBottom: '0.5rem', textAlign: 'center' }}>
            {message}
          </div>
          <div style={{ fontSize: '0.95rem', color: '#888', textAlign: 'center' }}>
            (This may take a few seconds if the server is waking up)
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AnalyticsTracker />
      <Routes>
        {/* Main App Routes with Authentication */}
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />

        <Route path="/*" element={
          user ? (
            // Layout for logged-in users
            <div style={{
              backgroundColor: theme.bgColor,
              color: theme.fgColor,
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              transition: "background-color 0.3s ease, color 0.3s ease"
            }}>
              <Header user={user} roles={roles} clubName={clubName} />
              <main style={{ flexGrow: 1 }}>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Core Routes */}
                    <Route path="/" element={<Home user={user} roles={roles} clubName={clubName} />} />
                    <Route path="/fixtures" element={<Fixtures />} />
                    <Route path="/results" element={<Results />} />
                    <Route path="/all-matches" element={<AllMatches />} />
                    <Route path="/tables" element={<Tables />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/minis" element={<MinisFixtures />} />
                    <Route path="/minis/admin" element={(roles.includes("club_admin") || roles.includes("super_admin")) ? <MinisAdmin /> : <Navigate to="/minis" replace />} />
                    <Route path="/pr" element={roles.includes("super_admin") || roles.includes("club_social") ? <PR clubName={clubName} /> : <Navigate to="/" replace />} />
                    <Route path="/resources/bookings" element={(roles.includes("super_admin") || roles.includes("club_admin")) ? <ResourceBookings roles={roles} /> : <Navigate to="/" replace />} />

                    {/* Club Admin Routes */}
                    <Route path="/volunteerRoles" element={roles.includes("club_admin") ? <VolunteerRoleAdmin /> : <Navigate to="/" replace />} />
                    <Route path="/teamAdmin" element={roles.includes("club_admin") ? <TeamAdmin /> : <Navigate to="/" replace />} />
                    <Route path="/resources/admin" element={roles.includes("club_admin") ? <ResourceAdmin /> : <Navigate to="/" replace />} />
                    <Route path="/website-embed" element={roles.includes("club_admin") ? <WebsiteEmbedAdminPage clubName={clubName} /> : <Navigate to="/" replace />} />

                    {/* Super Admin Routes */}
                    <Route path="/admin" element={roles.includes("super_admin") ? <AdminDashboard /> : <Navigate to="/" replace />} />

                    {/* Catch-all for logged-in users */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          ) : (
            // Redirect to login if not logged in
            <Navigate to="/login" replace />
          )
        } />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <SeasonProvider>
        <AppContent />
      </SeasonProvider>
    </AuthProvider>
  );
}

export default App;
